import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { CheckCircle, Clock, Truck, Package, Share2, Printer } from 'lucide-react'
import { orderService, settingsService } from '../services/api'
import { useCart } from '../contexts/CartContext'
import LoadingSpinner from '../components/LoadingSpinner'
import Currency from '../components/Currency'
import PrintableInvoice from '../components/PrintableInvoice'
import { getFeaturedImageUrl } from '../utils/imageUtils'

const OrderStatus = () => {
  const { orderNumber } = useParams()
  const { clearCart } = useCart()
  const [showPrintModal, setShowPrintModal] = useState(false)
  const cartClearedRef = useRef(false)
  
  // Fetch store settings for contact information
  const { data: settings } = useQuery({
    queryKey: ['storeSettings'],
    queryFn: settingsService.getSettings,
    retry: false,
  })
  
  // Clear cart when order status page loads (after successful payment)
  useEffect(() => {
    const clearCartAfterPayment = async () => {
      try {
        // Only clear cart if we have an order number and haven't cleared it yet
        if (orderNumber && !cartClearedRef.current) {
          cartClearedRef.current = true
          await clearCart()
          
          // Clean up any remaining payment data
          localStorage.removeItem('pendingOrder')
        }
      } catch (error) {
        // Don't show error to user for cart clearing issues
        console.warn('Cart clearing failed, but order was successful:', error)
        // Reset the flag so it can be retried if needed
        cartClearedRef.current = false
      }
    }
    
    clearCartAfterPayment()
  }, [orderNumber])
  
  // Handle case where order number is undefined
  if (!orderNumber) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-red-500 mb-4">
              <Package className="h-16 w-16 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">طلب غير صحيح</h2>
            <p className="text-gray-600 mb-6">
              لم يتم توفير رقم طلب. يرجى التحقق من الرابط أو العودة للصفحة الرئيسية.
            </p>
            <a
              href="/"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-700"
            >
              العودة للصفحة الرئيسية
            </a>
          </div>
        </div>
      </div>
    )
  }
  
  const { data: orderResponse, isLoading, error } = useQuery({
    queryKey: ['order', orderNumber],
    queryFn: () => orderService.getOrderByNumber(orderNumber),
    enabled: !!orderNumber,
    retry: 1
  })

  // Extract the order from the API response
  const order = orderResponse?.order

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-8 w-8 text-yellow-500" />
      case 'processing':
        return <Package className="h-8 w-8 text-blue-500" />
      case 'shipped':
        return <Truck className="h-8 w-8 text-purple-500" />
      case 'delivered':
        return <CheckCircle className="h-8 w-8 text-green-500" />
      default:
        return <Clock className="h-8 w-8 text-gray-500" />
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'تم استلام الطلب'
      case 'processing':
        return 'جاري تجهيز الطلب'
      case 'shipped':
        return 'تم شحن الطلب'
      case 'delivered':
        return 'تم تسليم الطلب'
      default:
        return 'حالة غير معروفة'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100'
      case 'processing':
        return 'text-blue-600 bg-blue-100'
      case 'shipped':
        return 'text-purple-600 bg-purple-100'
      case 'delivered':
        return 'text-green-600 bg-green-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const handleShare = async () => {
    const shareData = {
      title: `Order Status - #${order.order_number}`,
      text: `Check my order status: ${getStatusText(order.status)}`,
      url: window.location.href
    }

    try {
      if (navigator.share) {
        await navigator.share(shareData)
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href)
        toast.success('Order link copied to clipboard!')
      }
    } catch (error) {
      console.error('Error sharing:', error)
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(window.location.href)
        toast.success('Order link copied to clipboard!')
      } catch (clipboardError) {
        console.error('Clipboard error:', clipboardError)
      }
    }
  }

  const handlePrintInvoice = () => {
    setShowPrintModal(true)
  }

  const handleClosePrint = () => {
    setShowPrintModal(false)
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSpinner size="xl" className="py-12" />
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-red-500 mb-4">
              <Package className="h-16 w-16 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">طلب غير موجود</h2>
            <p className="text-gray-600 mb-6">
              لم يتم العثور على الطلب رقم #{orderNumber}. يرجى التحقق من رقم الطلب والمحاولة مرة أخرى.
            </p>
            
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">هل تحتاج مساعدة؟</h3>
                <p className="text-blue-800 text-sm mb-3">
                  إذا كنت تواجه مشكلة في الوصول إلى طلبك، يرجى التواصل معنا
                </p>
                <div className="text-sm text-blue-700">
                  {settings?.phone_number && (
                    <div>📞 الهاتف: {settings.phone_number}</div>
                  )}
                  {settings?.email && (
                    <div>📧 البريد: {settings.email}</div>
                  )}
                  {settings?.whatsapp_number && (
                    <div>💬 واتساب: {settings.whatsapp_number}</div>
                  )}
                  {!settings?.phone_number && !settings?.email && !settings?.whatsapp_number && (
                    <div className="text-gray-500">معلومات التواصل غير متوفرة حالياً</div>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="/"
                  className="inline-flex items-center px-6 py-3 border border-primary text-base font-medium rounded-md text-primary bg-white hover:bg-primary-50"
                >
                  العودة للصفحة الرئيسية
                </a>
                <a
                  href="/login"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-700"
                >
                  تسجيل الدخول
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">تفاصيل الطلب</h1>
            <p className="text-gray-600">رقم الطلب #{order.order_number}</p>
          </div>
          <div className="flex space-x-3 space-x-reverse">
            <button
              onClick={handlePrintInvoice}
              className="btn btn-primary flex items-center"
            >
              <Printer className="h-4 w-4 ml-2" />
              طباعة الفاتورة
            </button>
            <button
              onClick={handleShare}
              className="btn btn-secondary flex items-center"
            >
              <Share2 className="h-4 w-4 ml-2" />
              مشاركة
            </button>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="text-center">
            <div className="flex justify-center mb-2">
              {getStatusIcon(order.status)}
            </div>
            <p className="text-sm font-medium text-gray-900">{getStatusText(order.status)}</p>
            <p className="text-xs text-gray-500 mt-1">
              {new Date(order.created_at).toLocaleDateString('ar-SA')}
            </p>
          </div>
          
          <div className="flex-1 mx-4">
            <div className="h-2 bg-gray-200 rounded-full">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  order.status === 'pending' ? 'w-1/4 bg-yellow-500' :
                  order.status === 'processing' ? 'w-2/4 bg-blue-500' :
                  order.status === 'shipped' ? 'w-3/4 bg-purple-500' :
                  order.status === 'delivered' ? 'w-full bg-green-500' :
                  'w-0 bg-gray-500'
                }`}
              />
            </div>
          </div>
          
          <div className="text-center">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status || 'pending')}`}>
              {order.status ? (order.status.charAt(0).toUpperCase() + order.status.slice(1)) : 'Pending'}
            </span>
          </div>
        </div>
        
        <div className="text-center">
          <p className="text-gray-600">
            {(order.status === 'pending' || !order.status) && 'تم استلام طلبك وسيتم توصيله خلال 24 ساعة '}
            {order.status === 'processing' && 'جاري تجهيز طلبك وسيتم توصيله خلال 24 ساعة.'}
            {order.status === 'shipped' && 'الطلب خرج للتوصيل وهو في طريقه إليك.'}
            {order.status === 'delivered' && 'تم تسليم طلبك بنجاح!'}
          </p>
        </div>
      </div>

      {/* Order Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Order Items */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-medium text-gray-900">المنتجات المطلوبة</h2>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-4">
              {order.items?.length > 0 ? order.items.map((item, index) => (
                <div key={index} className="flex items-center space-x-4 space-x-reverse">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                      {getFeaturedImageUrl(item.featured_image) ? (
                        <img
                          src={getFeaturedImageUrl(item.featured_image)}
                          alt={item.product_name}
                          className="h-full w-full object-cover rounded-lg"
                        />
                      ) : (
                        <Package className="h-6 w-6 text-gray-400" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium text-gray-900">{item.product_name || 'منتج'}</h4>
                      {item.product_id && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          ID: {item.product_id}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">الكمية: {item.quantity}</p>
                    {item.product_id && (
                      <a 
                        href={`/products/${item.product_id}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        عرض المنتج ←
                      </a>
                    )}
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    <Currency amount={parseFloat(item.price) * item.quantity} />
                  </div>
                </div>
              )) : (
                <p className="text-gray-500">لم يتم العثور على منتجات</p>
              )}
            </div>
            
            <div className="border-t pt-4 mt-4 space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <p>المجموع الفرعي</p>
                <p><Currency amount={parseFloat(order.total_amount) - parseFloat(order.shipping_amount || 0)} /></p>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <p>تكلفة الشحن</p>
                <p><Currency amount={order.shipping_amount || 0} /></p>
              </div>
              <div className="flex justify-between text-base font-medium text-gray-900 border-t pt-2">
                <p>المجموع الكلي</p>
                <p><Currency amount={order.total_amount} /></p>
              </div>
            </div>
          </div>
        </div>

        {/* Order Information */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-medium text-gray-900">تفاصيل الطلب</h2>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700">تاريخ الطلب</h4>
              <p className="text-sm text-gray-900">
                {new Date(order.created_at).toLocaleDateString('ar-SA', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700">معلومات العميل</h4>
              <div className="text-sm text-gray-900">
                {order.name && (
                  <p>{order.name}</p>
                )}
                {order.email && <p>البريد الإلكتروني: {order.email}</p>}
                {order.phone && <p>الهاتف: {order.phone}</p>}
                {!order.name && !order.email && !order.phone && (
                  <p className="text-gray-500 italic">معلومات العميل غير متوفرة</p>
                )}
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700">عنوان الشحن</h4>
              <div className="text-sm text-gray-900">
                {order.shipping_address && <p>{order.shipping_address}</p>}
                {order.city_name && <p>{order.city_name}</p>}
                {order.governorate_name && <p>{order.governorate_name}</p>}
                {order.country_name && <p>{order.country_name}</p>}
                {!order.shipping_address && !order.city_name && (
                  <p className="text-gray-500 italic">عنوان الشحن غير متوفر</p>
                )}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700">طريقة الدفع</h4>
              <p className="text-sm text-gray-900">
                {order.payment_method === 'cash_on_delivery' ? 'الدفع عند الاستلام' : order.payment_method || 'غير محدد'}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Print Invoice Modal */}
      <PrintableInvoice
        order={order}
        isOpen={showPrintModal}
        onClose={handleClosePrint}
      />
    </div>
  )
}

export default OrderStatus
